<template>
  <Button v-bind="($props as any)" @click="onClick" :disabled="buttonDisabled">
    <slot> </slot>
    <div v-if="loading" class="loading-overlay">
      <Loader
        :type="loaderType"
        :theme-color="computedLoaderColor"
        :size="loaderSize"
      />
    </div>
  </Button>
</template>

<script setup lang="ts">
import { Button, type ButtonProps } from "@progress/kendo-vue-buttons";
import { Loader } from "@progress/kendo-vue-indicators";
import { computed } from "vue";

interface LoadingButtonProps extends ButtonProps {
  themeColor: string;
  loading: boolean;
  disableOnLoading?: boolean;
  loaderColor?: string;
  loaderType?: string;
  loaderSize?: string;
}

const defaultLoaderColors = new Map<string, string>([
  ["primary", "secondary"],
  ["secondary", "primary"],
  ["tertiary", "light"],
  ["info", "dark"],
  ["success", "inverse"],
  ["error", "dark"],
  ["warning", "inverse"],
  ["dark", "tertiary"],
  ["light", "tertiary"],
  ["inverse", "tertiary"],
]);

const props = withDefaults(defineProps<LoadingButtonProps>(), {
  loaderType: "pulsing",
  loaderSize: "large",
  disableOnLoading: true,
});

const buttonDisabled = computed(() => {
  return (props.disableOnLoading && props.loading) || props.disabled;
});

const computedLoaderColor = computed(() => {
  return (
    props.loaderColor || defaultLoaderColors.get(props.themeColor || "primary")
  );
});

const emit = defineEmits<{
  (event: "click", e: any): void;
}>();

const onClick = (...args: any[]) => {
  emit("click", args[0]);
};
</script>

<style scoped lang="scss">
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>
